import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./Students.css";
import initialStudentsBannerImage from '../novas-students-banner-template.jpeg';
import novasLogo from '../assets/novas-logo.png';
import { getStudentData, deleteStudent, getGroupsData, deleteGroup } from '../assets/data';
import { getStudents as getStudentsFromSupabase, deleteStudent as deleteStudentFromSupabase } from '../utils/studentsService';
import { getScheduleForStudent } from '../utils/scheduleService';
import useTeacherUnreadCounts from '../hooks/useTeacherUnreadCounts';

// Кэш списка студентов: не перезагружать при каждом заходе на вкладку
const STUDENTS_CACHE_TTL_MS = 90 * 1000;
const studentsCache = { students: {}, completedLessonsCount: {}, lastFetch: 0 };
const BATCH_SIZE = 12;

function Students() {
  const navigate = useNavigate();
  const [bannerImage, setBannerImage] = useState(initialStudentsBannerImage);
  const fileInputRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('Individual Students');
  const [students, setStudents] = useState({}); // Инициализируем пустым объектом, загружаем из Supabase
  const [groups, setGroups] = useState(getGroupsData());
  const [isLoadingStudents, setIsLoadingStudents] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [studentPhotos, setStudentPhotos] = useState({});
  const photoInputRefs = useRef({});
  const [completedLessonsCount, setCompletedLessonsCount] = useState({}); // Храним количество завершенных уроков для каждого студента
  const { byStudent: unreadByStudent } = useTeacherUnreadCounts();

  // Функция для подсчета завершенных уроков студента из расписания
  const getCompletedLessonsCount = (studentId) => {
    return completedLessonsCount[studentId] || 0;
  };

  // Функция для обработки загрузки фото
  const handlePhotoUpload = (studentId, event) => {
    event.stopPropagation(); // Предотвращаем клик по карточке
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setStudentPhotos(prev => ({
          ...prev,
          [studentId]: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Функция для клика по кнопке фото
  // eslint-disable-next-line no-unused-vars
  const handlePhotoButtonClick = (studentId, event) => {
    event.preventDefault();
    event.stopPropagation(); // Предотвращаем клик по карточке
    if (photoInputRefs.current[studentId]) {
      photoInputRefs.current[studentId].click();
    }
  };

  // Функция для правильного склонения слова "lesson"
  const getLessonText = (count) => {
    return count === 1 ? 'lesson' : 'lessons';
  };

  // Update active tab based on the tab name
  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
  };

  // Загрузка студентов: фаза 1 — список сразу, фаза 2 — счётчики уроков батчами
  const loadStudents = useCallback(async (useCacheIfValid = false, silentRefetch = false) => {
    const now = Date.now();
    const cacheValid = (now - studentsCache.lastFetch < STUDENTS_CACHE_TTL_MS) && Object.keys(studentsCache.students).length > 0;

    if (useCacheIfValid && cacheValid) {
      setStudents(studentsCache.students);
      setCompletedLessonsCount(studentsCache.completedLessonsCount || {});
      setIsLoadingStudents(false);
      return;
    }

    if (!silentRefetch) setIsLoadingStudents(true);

    try {
      // Фаза 1: только список студентов — быстрый первый рендер
      const studentsData = await getStudentsFromSupabase();
      setStudents(studentsData);
      studentsCache.students = studentsData;

      const studentEntries = Object.entries(studentsData);
      if (studentEntries.length === 0) {
        studentsCache.completedLessonsCount = {};
        studentsCache.lastFetch = Date.now();
        setIsLoadingStudents(false);
        return;
      }

      // Фаза 2: счётчики завершённых уроков батчами (порционно)
      const completedCounts = { ...(studentsCache.completedLessonsCount || {}) };
      setCompletedLessonsCount(completedCounts);

      for (let i = 0; i < studentEntries.length; i += BATCH_SIZE) {
        const batch = studentEntries.slice(i, i + BATCH_SIZE);
        const results = await Promise.all(
          batch.map(async ([slug, student]) => {
            try {
              const studentId = student.id || student.studentId || slug;
              const scheduleItems = await getScheduleForStudent(studentId);
              const doneCount = scheduleItems.filter(item =>
                item.status === 'done' && item.type === 'lesson'
              ).length;
              return [slug, doneCount];
            } catch {
              return [slug, 0];
            }
          })
        );
        results.forEach(([slug, count]) => { completedCounts[slug] = count; });
        setCompletedLessonsCount(current => ({ ...current, ...Object.fromEntries(results) }));
      }

      studentsCache.completedLessonsCount = completedCounts;
      studentsCache.lastFetch = Date.now();
    } catch (error) {
      console.error('Error loading students:', error);
      const fallbackData = getStudentData();
      setStudents(fallbackData || {});
      studentsCache.students = fallbackData || {};
    } finally {
      setIsLoadingStudents(false);
    }
  }, []);

  useEffect(() => {
    console.log('NOVAS Logo Path:', novasLogo);
  }, []);

  // Обновляем данные при изменении localStorage (только для групп, так как студенты теперь в Supabase)
  useEffect(() => {
    const handleStorageChange = () => {
      // Обновляем только группы из localStorage, студенты загружаются из Supabase
      setGroups(getGroupsData());
    };

    // Обработчик для обновления счетчика завершенных уроков при изменении расписания
    const handleScheduleUpdate = () => {
      studentsCache.lastFetch = 0; // инвалидировать кэш
      loadStudents(false, false);
    };
    const handleStudentsUpdated = () => {
      studentsCache.lastFetch = 0;
      loadStudents(false, false);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('scheduleUpdated', handleScheduleUpdate);
    window.addEventListener('studentsUpdated', handleStudentsUpdated);
    window.addEventListener('novas-students-updated', handleStudentsUpdated);
    
    // Также обновляем при изменении localStorage в том же окне
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function(key, value) {
      originalSetItem.apply(this, arguments);
      // Обновляем только группы, студенты теперь в Supabase
      if (key === 'novas_groups_data') {
        handleStorageChange();
      }
    };

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('scheduleUpdated', handleScheduleUpdate);
      window.removeEventListener('studentsUpdated', handleStudentsUpdated);
      window.removeEventListener('novas-students-updated', handleStudentsUpdated);
      localStorage.setItem = originalSetItem;
    };
  }, [loadStudents]);

  const handleBannerUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleBannerFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleStudentClick = (studentSlug) => {
    navigate(`/students/${studentSlug}`);
  };

  const handleAddNew = () => {
    setShowAddModal(true);
  };

  const handleAddStudent = () => {
    setShowAddModal(false);
    navigate('/students/new');
  };

  const handleAddGroup = () => {
    setShowAddModal(false);
    navigate('/groups/new');
  };

  // eslint-disable-next-line no-unused-vars
  const handleDeleteStudent = async (e, studentSlug, studentName) => {
    e.stopPropagation(); // Prevent navigation when clicking delete
    
    if (window.confirm(`Are you sure you want to delete ${studentName}'s profile? This action cannot be undone.`)) {
      try {
        // Находим студента по slug
        const student = Object.values(students).find(s => s.slug === studentSlug || s.invite_slug === studentSlug);
        if (student && student.id) {
          await deleteStudentFromSupabase(student.id);
          // Обновляем список студентов
          loadStudents();
        } else {
          // Fallback на старый метод для совместимости
          deleteStudent(studentSlug);
          setStudents(getStudentData());
        }
      } catch (error) {
        console.error('Error deleting student:', error);
        alert('Error deleting student: ' + error.message);
      }
    }
  };

  // При монтировании: использовать кэш если свежий, иначе загрузить
  useEffect(() => {
    loadStudents(true, false); // useCacheIfValid = true
  }, [loadStudents]);

  // Refetch при возврате на вкладку (visibility), если кэш устарел
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - studentsCache.lastFetch >= STUDENTS_CACHE_TTL_MS || Object.keys(studentsCache.students).length === 0) {
        loadStudents(false, true); // silent refetch
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [loadStudents]);

  const handleGroupClick = (groupId) => {
    navigate(`/groups/${groupId}`);
  };

  // eslint-disable-next-line no-unused-vars
  const handleDeleteGroup = (e, groupId, groupName) => {
    e.stopPropagation(); // Prevent navigation when clicking delete
    
    if (window.confirm(`Are you sure you want to delete ${groupName}'s profile? This action cannot be undone.`)) {
      deleteGroup(groupId);
      setGroups(getGroupsData());
    }
  };

  const filteredStudents = Object.entries(students).filter(([slug, student]) =>
    student.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredGroups = Object.entries(groups).filter(([groupId, group]) =>
    group.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="students-page">
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleBannerFileChange}
        accept="image/*"
      />
      <div
        className="students-banner"
        style={{ backgroundImage: `url(${bannerImage})` }}
        onClick={handleBannerUploadClick}
      >
        <img src={novasLogo} alt="NOVAS Logo" className="students-logo" />
        <div className="banner-content">
          <h1>Students</h1>
          <p>Manage all your students' profiles and lessons here.</p>
        </div>
        <div className="students-banner-upload-overlay">Click to change banner</div>
      </div>

      <div className="students-content">
        <div className="filter-sort-bar">
          <div className="filter-group">
            {/* Tab switcher buttons */}
            <div className="top-tab-switcher">
              <button
                className={`top-tab-button ${activeTab === 'Individual Students' ? 'active' : ''}`}
                onClick={() => handleTabChange('Individual Students')}
              >
                Individual
              </button>
              <button
                className={`top-tab-button ${activeTab === 'Groups' ? 'active' : ''}`}
                onClick={() => handleTabChange('Groups')}
              >
                Groups
              </button>
            </div>
            
            <div className="search-input-group">
              <input
                type="text"
                placeholder={activeTab === 'Individual Students' ? "Search students" : "Search groups"}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="students-search-input"
              />
              <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
            </div>
          </div>
          <div className="view-and-add">
            <button className="add-new-button" onClick={handleAddNew}>+ Add new</button>
          </div>
        </div>


        {activeTab === 'Individual Students' && isLoadingStudents ? (
          // Rendered as a sibling of the grid, not a grid item inside it
          // (grid-column: 1/-1 spanning a single auto-fill item, with
          // nothing else in the grid while loading, was landing
          // off-center on mobile). Page-specific class names too —
          // matches Schedule's own .schedule-loading pattern, which
          // never has this problem because nothing else in the app
          // defines those names.
          <div className="students-loading">
            <div className="students-loading-spinner"></div>
            <p className="students-loading-text">Loading students...</p>
          </div>
        ) : (
        <div className="student-folders-grid">
          {activeTab === 'Individual Students' && (
            filteredStudents.length > 0 ? (
              filteredStudents.map(([slug, student]) => {
                const studentUuid = student.id || student.studentId || slug;
                const unread = unreadByStudent[studentUuid]?.total || 0;
                return (
                <div key={slug} className="student-card-wrapper">
                  {unread > 0 && (
                    <span className="student-card-unread-badge">{unread > 99 ? '99+' : unread}</span>
                  )}
                  <div
                    className="student-folder-card"
                    onClick={() => handleStudentClick(slug)}
                    data-photo={studentPhotos[slug] ? 'true' : 'false'}
                    style={studentPhotos[slug] ? { '--student-photo': `url(${studentPhotos[slug]})` } : {}}
                  >
                    <div className="folder-icon">
                      <img
                        src={`https://via.placeholder.com/150/FF5733/FFFFFF?text=${student.name.split(' ').map(n => n[0]).join('')}`}
                        alt={student.name}
                        className="student-avatar-thumbnail"
                      />
                    </div>
                    <div className="student-info">
                      <h3 
                        className="student-name" 
                        style={{ 
                          color: '#000000', 
                          fontWeight: '700',
                          fontSize: '1.1rem',
                          textShadow: 'none'
                        }}
                      >
                        {student.name}
                      </h3>
                      <p className="lesson-count">{getCompletedLessonsCount(slug)} {getLessonText(getCompletedLessonsCount(slug))}</p>
                    </div>
                  </div>
                  
                  {/* Скрытый input для загрузки фото */}
                  <input
                    type="file"
                    accept="image/*"
                    ref={el => photoInputRefs.current[slug] = el}
                    onChange={(e) => handlePhotoUpload(slug, e)}
                    style={{ display: 'none' }}
                  />
                  
                  {/* Кнопка загрузки фото - вынесена за пределы карточки */}
                  <div 
                    className="photo-upload-button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (photoInputRefs.current[slug]) {
                        photoInputRefs.current[slug].click();
                      }
                    }}
                  ></div>
                </div>
                );
              })
            ) : (
              <p className="no-students-found">No students found matching your search.</p>
            )
          )}
          {activeTab === 'Groups' && (
            filteredGroups.length > 0 ? (
              filteredGroups.map(([groupId, group]) => (
                <div key={groupId} className="student-card-wrapper">
                  <div
                    className="student-folder-card"
                    onClick={() => handleGroupClick(groupId)}
                    data-photo={studentPhotos[groupId] ? 'true' : 'false'}
                    style={studentPhotos[groupId] ? { '--student-photo': `url(${studentPhotos[groupId]})` } : {}}
                  >
                    <div className="folder-icon">
                      <img
                        src={`https://via.placeholder.com/150/FF5733/FFFFFF?text=${group.name.split(' ').map(n => n[0]).join('')}`}
                        alt={group.name}
                        className="student-avatar-thumbnail"
                      />
                    </div>
                    <div className="student-info">
                      <h3 
                        className="student-name" 
                        style={{ 
                          color: '#000000', 
                          fontWeight: '700',
                          fontSize: '1.1rem',
                          textShadow: 'none'
                        }}
                      >
                        {group.name}
                      </h3>
                      <p className="lesson-count">
                        {group.participants ? group.participants.length : 0} participants
                      </p>
                    </div>
                  </div>
                  
                  {/* Скрытый input для загрузки фото */}
                  <input
                    type="file"
                    accept="image/*"
                    ref={el => photoInputRefs.current[groupId] = el}
                    onChange={(e) => handlePhotoUpload(groupId, e)}
                    style={{ display: 'none' }}
                  />
                  
                  {/* Кнопка загрузки фото - вынесена за пределы карточки */}
                  <div 
                    className="photo-upload-button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (photoInputRefs.current[groupId]) {
                        photoInputRefs.current[groupId].click();
                      }
                    }}
                  ></div>
                </div>
              ))
            ) : (
              <p className="no-students-found">No groups found matching your search.</p>
            )
          )}
        </div>
        )}
      </div>

      {/* Add New Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New</h2>
              <button 
                className="modal-close-btn" 
                onClick={() => setShowAddModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="add-options">
                <button 
                  className="add-option-btn student-option"
                  onClick={handleAddStudent}
                >
                  <div className="option-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                  </div>
                  <div className="option-content">
                    <h3>New Student</h3>
                    <p>Create a new individual student profile</p>
                  </div>
                </button>
                <button 
                  className="add-option-btn group-option"
                  onClick={handleAddGroup}
                >
                  <div className="option-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                  </div>
                  <div className="option-content">
                    <h3>New Group</h3>
                    <p>Create a new group with existing students</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Students; 